/**
 * Sign In Page Component (September 2025)
 * 
 * Landing page with Clerk authentication for users to sign in/sign up.
 * Provides a clean interface for authentication with Key Palace branding.
 */

import React, { useRef } from 'react';
import { SignUpButton, SignedIn, SignedOut, useAuth } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';

interface SignInPageProps {}

export function SignInPage({}: SignInPageProps) {
  const navigate = useNavigate();
  const signUpButtonRef = useRef<HTMLButtonElement>(null);
  const { isSignedIn, isLoaded } = useAuth();
  
  // Use React.ComponentType to resolve type compatibility with React 19
  const SignedInCompat = SignedIn as React.ComponentType<{ children?: React.ReactNode }>;
  const SignedOutCompat = SignedOut as React.ComponentType<{ children?: React.ReactNode }>;

  // Redirect to dashboard page when user is signed in
  React.useEffect(() => {
    if (isLoaded && isSignedIn) {
      // Navigate directly to the dashboard page without showing any message
      navigate({ to: '/app/dashboard' }).catch((error) => {
        console.error('Navigation failed:', error);
        // Fallback to window.location if router navigation fails
        window.location.href = '/app/dashboard';
      });
    }
  }, [isLoaded, isSignedIn, navigate]);

  // Add keyboard listener for 'y' key
  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'y' && signUpButtonRef.current) {
        signUpButtonRef.current.click();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Show loading state while Clerk initializes
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center terminal-status">
          <div className="terminal-glow text-terminal-cyan">
            [INITIALIZING AUTHENTICATION...]
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden">
      <SignedInCompat>
        {/* Return null - navigation handled by useEffect */}
        {null}
      </SignedInCompat>
      
      <SignedOutCompat>
        <div className="h-full p-4 sm:p-6 max-w-full overflow-hidden flex flex-col">
          {/* ASCII Art Logo - Moved to top */}
          <div className="text-center mb-3 sm:mb-4 overflow-hidden max-w-full flex-shrink-0">
            <div className="ascii-art text-[6px] xs:text-[8px] sm:text-[10px] md:text-xs lg:text-sm xl:text-base leading-tight whitespace-pre font-mono mx-auto w-fit max-w-full">
{`██╗  ██╗███████╗██╗   ██╗    ██████╗  █████╗ ██╗      █████╗  ██████╗███████╗
██║ ██╔╝██╔════╝╚██╗ ██╔╝    ██╔══██╗██╔══██╗██║     ██╔══██╗██╔════╝██╔════╝
█████╔╝ █████╗   ╚████╔╝     ██████╔╝███████║██║     ███████║██║     █████╗
██╔═██╗ ██╔══╝    ╚██╔╝      ██╔═══╝ ██╔══██║██║     ██╔══██║██║     ██╔══╝
██║  ██╗███████╗   ██║       ██║     ██║  ██║███████╗██║  ██║╚██████╗███████╗
╚═╝  ╚═╝╚══════╝   ╚═╝       ╚═╝     ╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝ ╚═════╝╚══════╝`}
            </div>
            <h1 className="typewriter-text text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl mt-2 sm:mt-3 mb-4 sm:mb-6 terminal-glow mx-auto leading-tight max-w-[1000px] whitespace-normal">
              For your saas product: manage credit systems, scheduled refills, spending limits, & more
            </h1>
          </div>
          
          <div className="flex-1 flex items-center justify-center overflow-hidden">
            <div className="text-center max-w-4xl w-full px-2 flex flex-col justify-center h-full">

            {/* Features in Terminal Style */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-6 sm:mb-8 text-left font-mono max-w-2xl mx-auto">
              <div className="terminal-status text-base sm:text-lg">► SECURE API KEY MANAGEMENT</div>
              <div className="terminal-status text-base sm:text-lg">► REAL-TIME CREDIT TRACKING /W ATOMIC TRANSACTIONS</div>
              <div className="terminal-status text-base sm:text-lg">► CLIENT KEY CREATION & MANAGEMENT</div>
              <div className="terminal-status text-base sm:text-lg">► FREE TIER: 100 CLIENT API KEYS WITH CREDIT MANAGEMENT & METADATA</div>
            </div>

            {/* Central Sign Up Button */}
            <div className="mb-6 sm:mb-8">
              <SignUpButton>
                <button 
                  ref={signUpButtonRef}
                  className="terminal-button text-xl sm:text-2xl px-6 sm:px-8 py-2 sm:py-3 terminal-glow hover:scale-105 transition-all duration-200"
                >
                  <span className="button-typewriter-text">Get started?(y/n)</span>
                </button>
              </SignUpButton>
            </div>

            {/* Legal Text */}
            <div className="text-xs text-terminal-orange-dim border-t border-terminal-orange-dim pt-4 sm:pt-6 px-4">
              <p className="mb-2">© 2025, Key Palace</p>
              <p className="flex flex-col sm:flex-row sm:justify-center gap-2 sm:gap-0">
                <a href="#" className="text-terminal-orange hover:text-terminal-orange-bright transition-colors">
                  [TERMS OF SERVICE]
                </a>
                <span className="hidden sm:inline">{' | '}</span>
                <a href="#" className="text-terminal-orange hover:text-terminal-orange-bright transition-colors">
                  [PRIVACY POLICY]
                </a>
              </p>
            </div>
            </div>
          </div>
        </div>
      </SignedOutCompat>
    </div>
  );
}